import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Fish, Plus, Trash, Pencil, Check, X as XIcon, List as ListIcon,
  CalendarDots, ChartLine, FishSimple, Images, SignOut, House, Receipt
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminGalleryPage = () => {
  const { token, logout } = useAuth();
  const location = useLocation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editCaptions, setEditCaptions] = useState({ caption_fr: '', caption_en: '', caption_it: '' });
  const [formData, setFormData] = useState({ image_url: '', caption_fr: '', caption_en: '', caption_it: '' });

  const fetchImages = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/gallery`, { headers: { Authorization: `Bearer ${token}` } });
      setImages(r.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const handleAddImage = async () => {
    if (!formData.image_url) { toast.error("URL de l'image requise"); return; }
    try {
      await axios.post(`${API}/admin/gallery`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Image ajoutée');
      setAddDialogOpen(false);
      setFormData({ image_url: '', caption_fr: '', caption_en: '', caption_it: '' });
      fetchImages();
    } catch (e) { toast.error("Erreur lors de l'ajout"); }
  };

  const handleDeleteImage = async () => {
    if (!selectedImage) return;
    try {
      await axios.delete(`${API}/admin/gallery/${selectedImage.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Image supprimée');
      setDeleteDialogOpen(false); setSelectedImage(null); fetchImages();
    } catch (e) { toast.error("Erreur"); }
  };

  const startEdit = (image) => {
    setEditingId(image.id);
    setEditCaptions({ caption_fr: image.caption_fr || '', caption_en: image.caption_en || '', caption_it: image.caption_it || '' });
  };

  const saveEdit = async (imageId) => {
    try {
      await axios.put(`${API}/admin/gallery/${imageId}`, editCaptions, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Légende mise à jour');
      setEditingId(null);
      fetchImages();
    } catch (e) { toast.error("Erreur"); }
  };

  const navItems = [
    { path: '/admin/dashboard', icon: CalendarDots, label: 'Agenda' },
    { path: '/admin/invoices', icon: Receipt, label: 'Factures' },
    { path: '/admin/trips', icon: FishSimple, label: 'Sorties Pêche' },
    { path: '/admin/gallery', icon: Images, label: 'Galerie' },
    { path: '/admin/analytics', icon: ChartLine, label: 'Analytics' },
  ];
  const isCurrentPage = (path) => location.pathname === path;

  if (loading) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-gallery-page">
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-[#000000] text-white z-30">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/mw45n5je_Logo%20Calvi%20Fishing.png" alt="Calvi Fishing" className="h-10 w-10 object-contain rounded-full" />
            <span className="text-lg font-bold font-['Playfair_Display']">CALVI FISHING</span>
          </Link>
        </div>
        <nav className="mt-6 px-3">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${isCurrentPage(item.path) ? 'bg-[#b7ede0] text-[#000000] font-semibold' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
              <item.icon size={20} /><span className="text-sm font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-6 space-y-2">
          <Link to="/"><Button variant="ghost" className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5"><House size={20} className="mr-3" />Voir le site</Button></Link>
          <Button variant="ghost" onClick={logout} className="w-full justify-start text-gray-400 hover:text-[#FF3B30] hover:bg-red-500/10"><SignOut size={20} className="mr-3" />Déconnexion</Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#000000] text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_calvi-fishing-book/artifacts/mw45n5je_Logo%20Calvi%20Fishing.png" alt="Calvi Fishing" className="h-8 w-8 object-contain rounded-full" />
            <span className="font-bold font-['Playfair_Display']">CALVI FISHING</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white">{mobileMenuOpen ? <XIcon size={24} /> : <ListIcon size={24} />}</Button>
        </div>
        {mobileMenuOpen && (
          <nav className="mt-3 pb-3 border-t border-white/10 pt-3 space-y-1">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isCurrentPage(item.path) ? 'bg-[#b7ede0] text-[#000000] font-semibold' : 'text-gray-400'}`}>
                <item.icon size={18} /><span className="text-sm">{item.label}</span>
              </Link>
            ))}
          </nav>
        )}
      </header>

      <main className="lg:ml-64 pt-16 lg:pt-0 p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-[#000000] font-['Playfair_Display']">Gestion de la Galerie</h1>
          <Button onClick={() => setAddDialogOpen(true)} className="bg-[#b7ede0] hover:bg-[#9ee5d4] text-[#000000] rounded-full gap-2" data-testid="add-image-btn">
            <Plus size={18} />Ajouter une image
          </Button>
        </div>

        {images.length === 0 ? (
          <Card><CardContent className="py-20 text-center"><p className="text-[#4B5563]">Aucune image dans la galerie</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {images.map((image) => (
              <Card key={image.id} className="overflow-hidden group">
                <div className="relative aspect-square">
                  <img src={image.image_url} alt={image.caption_fr || 'Gallery'} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button size="sm" onClick={() => startEdit(image)} className="bg-[#b7ede0] text-[#000000] gap-1" data-testid={`edit-image-${image.id}`}>
                      <Pencil size={14} />Modifier
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => { setSelectedImage(image); setDeleteDialogOpen(true); }} data-testid={`delete-image-${image.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-3">
                  {editingId === image.id ? (
                    <div className="space-y-2">
                      <Input value={editCaptions.caption_fr} onChange={e => setEditCaptions({ ...editCaptions, caption_fr: e.target.value })} placeholder="Légende FR" className="text-sm h-8" data-testid="edit-caption-fr" />
                      <Input value={editCaptions.caption_en} onChange={e => setEditCaptions({ ...editCaptions, caption_en: e.target.value })} placeholder="Caption EN" className="text-sm h-8" />
                      <Input value={editCaptions.caption_it} onChange={e => setEditCaptions({ ...editCaptions, caption_it: e.target.value })} placeholder="Didascalia IT" className="text-sm h-8" />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveEdit(image.id)} className="bg-[#b7ede0] text-[#000000] gap-1"><Check size={14} />Sauver</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}><XIcon size={14} /></Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-[#4B5563] truncate">{image.caption_fr || 'Sans légende'}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Playfair_Display']">Ajouter une image</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>URL de l'image *</Label><Input value={formData.image_url} onChange={e => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://..." className="mt-1" /></div>
            <div><Label>Légende FR</Label><Input value={formData.caption_fr} onChange={e => setFormData({ ...formData, caption_fr: e.target.value })} placeholder="Belle prise du jour !" className="mt-1" /></div>
            <div><Label>Légende EN</Label><Input value={formData.caption_en} onChange={e => setFormData({ ...formData, caption_en: e.target.value })} placeholder="Great catch!" className="mt-1" /></div>
            <div><Label>Légende IT</Label><Input value={formData.caption_it} onChange={e => setFormData({ ...formData, caption_it: e.target.value })} placeholder="Bella cattura!" className="mt-1" /></div>
            {formData.image_url && <div className="border rounded-lg overflow-hidden"><img src={formData.image_url} alt="Preview" className="w-full h-48 object-cover" onError={e => { e.target.style.display = 'none'; }} /></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddDialogOpen(false)}>Annuler</Button><Button onClick={handleAddImage}>Ajouter</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Supprimer l'image</DialogTitle><DialogDescription>Êtes-vous sûr de vouloir supprimer cette image ?</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Annuler</Button><Button variant="destructive" onClick={handleDeleteImage}>Supprimer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGalleryPage;
